import React, { useState, useEffect, useContext } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ICONS, PATHS } from 'src/common/constants';
import Avatar from '../../components/Avatar';
import Navigation from '../../components/Navigation';
import * as sidebar from '../../copy/sidebar';
import Tenant from '../../components/Tenant';
import Typeahead from './Typeahead';
import Lightbox from 'react-image-lightbox';
import 'react-image-lightbox/style.css';
import FontAwesomeIcon from '../../components/Icon';
import classNames from 'classnames';
import { Images } from 'src/Tenants';
import { checkUserPermission } from 'src/helpers/validationHelper';
import Button1 from '../../components/Button/Button1';
import SuiteInfoHeader from '../../components/HomeOwnerDashboard/SuiteInfoHeader';
import {
  clearSelectedModulePermission,
  fetchtAllowedTabList,
} from 'src/store/actions/tabSelections';
import { store } from 'src/store';
import Loader from '../../components/Loader';
import ServiceRequestForm from '../../components/ServiceRequestForm/ServiceRequestForm';
import { selectServiceRequestFormIsVisible } from 'src/store/selectors/serviceRequestForm';
import { setServiceRequestFormIsVisible } from 'src/store/actions/serviceRequestForm';
import Overlay from '../../components/Overlay';
import { BrandingContext } from 'src/branding/BrandingContextProvider';
import { ContactFormProvider } from 'src/SharedCommonContext/ContactFormContext';
import Wizard from 'src/components/Wizard/Wizard';
import { PropertiesForContactsProvider } from 'src/pages/Contacts/ContactsContext/PropertiesForContactsContext';
import './Sidebar.scss';

const sideNavOption = [
  {
    localLabel: 'Announcements',
    label: 'Announcements',
    path: '/app/announcements',
    icon: 'bullhorn',
  },
];

interface SidebarProps {
  from?: any;
  serviceRequest?: any;
  user: any;
  collapsableSideBar: () => void;
  collapseSideBar: boolean;
  propertyDisabled?: boolean;
}

interface LinkItem {
  label: string;
  path: string;
  icon: string;
  id?: string;
  localLabel?: string;
}

const Sidebar = ({
  from,
  serviceRequest,
  user,
  collapsableSideBar,
  collapseSideBar,
  propertyDisabled,
}: SidebarProps) => {
  // Replace state with useState hooks.
  const [showOverlay, setShowOverlay] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [topLinks, setTopLinks] = useState<LinkItem[]>([]);
  const [bottomLinks, setBottomLinks] = useState<LinkItem[]>([]);
  const [loader, setLoader] = useState<boolean>(false);
  const [ishomeOwnwerHasAcessButton, setIshomeOwnwerHasAcessButton] =
    useState<boolean>(false);

  // Replace mapStateToProps with useSelector hooks.
  const tabSelections: any = useSelector((state: any) => state.tabSelections);
  const selectedUserSuite: any = useSelector(
    (state: any) => state.userSuites.selectedUserSuite
  );
  const onboardingData: any = useSelector((state: any) => state.onboardingData);
  const serviceRequestFormIsVisible: any = useSelector(
    selectServiceRequestFormIsVisible
  );

  // Replace mapDispatchToProps with useDispatch hook
  const dispatch = useDispatch();
  const onSetServiceRequestFormIsVisible = (isVisible: boolean) =>
    dispatch(setServiceRequestFormIsVisible(isVisible));
  const fetchAllowedTabListAction = (propertyId: string) =>
    dispatch(fetchtAllowedTabList(propertyId));
  const clearSelectedModulePermissionAction = () =>
    dispatch(clearSelectedModulePermission());

  // Replace contextType with useContext hook.
  const { branding } = useContext(BrandingContext);

  // Method to update localStorage flag.
  const updateHomeownerDashboardFlag = (
    user: any,
    showHomeownerDashboard: boolean
  ) => {
    localStorage.removeItem('showHomeownerDasboardPage'); // Should fix this typo, but I'm too scared to touch it for now lol.

    if (user.isDashboardAccess) {
      localStorage.setItem(
        'showHomeownerDasboardPage',
        String(showHomeownerDashboard)
      );
    }
  };

  // Add calendar navigation option if user has access.
  const addCalendarOptionIfAllowed = (links: LinkItem[], user: any) => {
    const userRole: any = user.roles[0].role.name;

    const userCanAccessCalendar: boolean = [
      'Admin',
      'Super Admin',
      'Property Developer',
      'Service Provider',
      'Service Manager'
    ].includes(userRole);

    if (userCanAccessCalendar) {
      const calendarOption = {
        label: 'Calendar', // Human-readable label, not filename.
        path: PATHS.CALENDAR,
        icon: 'calendar',
        id: 'calendar',
      };

      // Insert calendar as the second item (index 1) if links array has items. Otherwise, just add it to the array
      if (links.length > 0) {
        const newLinks: LinkItem[] = [...links];
        newLinks.splice(1, 0, calendarOption); // Insert at index 1 (second position).
        return newLinks;
      } else {
        return [calendarOption];
      }
    }

    // Otherwise return the existing links as is.
    return links;
  };

  // Get allowed navigation options from tab list
  const getAllowedNavigationOptions = (allowedTabList: any[]) => {
    return allowedTabList
      .map((item: any) =>
        sideNavOption.find((tn: any) => tn.localLabel === item.name)
      )
      .filter((element: any) => element !== undefined);
  };

  // Remove duplicate links by label
  const removeDuplicateLinks = (links: LinkItem[]) => {
    return links.reduce((acc: LinkItem[], current: LinkItem) => {
      const exists: LinkItem = acc.find(
        (item: LinkItem) => item.label === current.label
      );
      return exists ? acc : [...acc, current];
    }, []);
  };

  // Handle role-specific links (for Homeowner and Realtor).
  const handleRoleSpecificLinks = (
    links: LinkItem[],
    allowedNavOptions: any[],
    user: any,
    showHomeownerDashboard: boolean
  ): LinkItem[] => {
    // Add announcement links if allowed.
    const announcementLinks: any[] = allowedNavOptions.filter(
      (item: any) => item && item.localLabel === 'Announcements'
    );

    // Combine and deduplicate links.
    const combinedLinks: any = [...links, ...announcementLinks];
    const uniqueLinks: LinkItem[] = removeDuplicateLinks(combinedLinks);

    // Only Realtors should have the dashboard access check.
    if (user.isDashboardAccess) {
      return uniqueLinks.filter((item: LinkItem) => item.label === 'Dashboard');
    }

    return showHomeownerDashboard
      ? uniqueLinks
      : uniqueLinks.filter((item: LinkItem) => item.id !== 'dashboard');
  };

  // Handle user-specific links based on role
  const handleUserSpecificLinks = (
    links: LinkItem[],
    allowedTabList: any[],
    user: any,
    showHomeownerDashboard: boolean
  ): LinkItem[] => {
    const allowedNavOptions: any = getAllowedNavigationOptions(allowedTabList);

    // No allowed nav options case
    if (allowedNavOptions.length === 0) {
      return showHomeownerDashboard
        ? links
        : links.filter((item: LinkItem) => item.id !== 'dashboard');
    }

    const userRole = user.roles[0].role.name;

    // Handle both Homeowner and Realtor with the same function
    if (userRole === 'Home Owner' || userRole === 'Realtor') {
      return handleRoleSpecificLinks(
        links,
        allowedNavOptions,
        user,
        showHomeownerDashboard
      );
    }

    // Default case for other roles.
    return links;
  };

  // Method to get the top links for the current user.
  const getTopLinksForUser = (
    user: any,
    branding: any,
    allowedTabList: any[],
    showHomeownerDashboard: boolean
  ) => {
    const userRole: any = user.roles[0].role.name;

    // Get initial top links based on dashboard access.
    let topLinks = user.isDashboardAccess
      ? sidebar
          .topLinks(branding)
          .filter((item: any) => item.label === 'Dashboard')
      : sidebar.topLinks(branding);

    // Filter by permissions.
    let links: any = topLinks.filter((link: any) =>
      checkUserPermission(link.label, false, link.id)
    );

    // Filter out nav links for 'dashboard' 'keyContacts' and 'home' for Admins.
    if (['Admin', 'Super Admin'].includes(userRole)) {
      links = links.filter(
        (nav: any) =>
          nav.id !== 'dashboard' &&
          nav.id !== 'keyContacts' &&
          nav.id !== 'home'
      );
    }

    // Add calendar options if user has access after filtering.
    links = addCalendarOptionIfAllowed(links, user);

    // Handle homeowner and realtor specific logic.
    if (
      (userRole === 'Home Owner' || userRole === 'Realtor') &&
      !user.isDashboardAccess
    ) {
      return handleUserSpecificLinks(
        links,
        allowedTabList,
        user,
        showHomeownerDashboard
      );
    }

    // Apply dashboard access filter.
    return user.isDashboardAccess
      ? links.filter((nav) => nav.id === 'dashboard')
      : links;
  };

  const getBottomLinksForUser = (user: any, branding: any) => {
    const userRole: any = user.roles[0].role.name;
    let bottomLinks: any = sidebar.bottomLinks(branding);

    // Only admins and developers can access email templates.
    const adminRoles: string[] = ['Admin', 'Super Admin', 'Property Developer'];

    if (!adminRoles.includes(userRole)) {
      bottomLinks = bottomLinks.filter(
        (nav: any) => nav.id !== 'emailTemplates'
      );
    }

    return bottomLinks;
  };

  const setSideMenu = () => {
    const { allowedTabList } = store.getState().tabSelections;
    const { orders } = store.getState().onboardingData;
    const showHomeownerDashboard: boolean =
      Object.keys(orders).length > 0 ? true : false;

    updateHomeownerDashboardFlag(user, showHomeownerDashboard);

    const newTopLinks: any = getTopLinksForUser(
      user,
      branding,
      allowedTabList,
      showHomeownerDashboard
    );
    const newBottomLinks: any = getBottomLinksForUser(user, branding);

    // Update our state with the new links.
    setTopLinks(newTopLinks);
    setBottomLinks(newBottomLinks);
    setLoader(false);
  };

  // useEffect equivalent of componentDidMount.
  useEffect(() => {
    window.setTimeout(() => setSideMenu(), 200);
  }, []);

  // useEffect equivalent of componentDidUpdate.
  useEffect(() => {
    const userRole: any = user.roles[0].role.name;

    if (
      tabSelections !== tabSelections &&
      selectedUserSuite.propertyId !== selectedUserSuite.propertyId
    ) {
      setSideMenu();
    }

    if (
      ishomeOwnwerHasAcessButton === false &&
      (userRole === 'Home Owner' || userRole === 'Occupant')
    ) {
      let selectedUserSuiteId: any = selectedUserSuite?.propertyId;
      let IsHomeOwnerProperties: any = store.getState().properties;

      if (IsHomeOwnerProperties.length > 0) {
        const homeOwnerpermissions: any = IsHomeOwnerProperties.filter(
          (x: any) => x.propertyId === selectedUserSuiteId
        );

        if (homeOwnerpermissions.length > 0) {
          if (homeOwnerpermissions[0].modulePermissions.length > 0) {
            let filterData: any =
              homeOwnerpermissions[0].modulePermissions.filter(
                (x: any) => x.name === 'Service Requests'
              );

            if (filterData.length > 0) {
              setIshomeOwnwerHasAcessButton(true);
            }
          }
        }
      }
    }
  }, [user, tabSelections, selectedUserSuite, ishomeOwnwerHasAcessButton]);

  // Additional useEffect to handle property ID changes
  useEffect(() => {
    if (selectedUserSuite && selectedUserSuite.propertyId) {
      setLoader(true);
      fetchAllowedTabListAction(selectedUserSuite.propertyId);
    }
  }, [selectedUserSuite?.propertyId]);

  // Additional useEffect to handle onboardingData changes
  useEffect(() => {
    if (onboardingData && onboardingData.orders) {
      setLoader(true);
      setTimeout(() => setSideMenu(), 3000);
    }
  }, [onboardingData.orders]);

  return (
    <>
      {loader ? <Loader center={true} /> : null}
      <div
        className={classNames('sidebar__collapsableBar', {
          'sidebar__collapsableBar--collapsedSideBar': collapseSideBar,
        })}
      >
        <div
          onClick={() => collapsableSideBar()}
          className="sidebar__collapsableBar--Icon"
        >
          <FontAwesomeIcon weight="solid" glyph="bars" />
        </div>
      </div>
      <div
        className={classNames('sidebar', {
          'sidebar--collapsed': collapseSideBar,
        })}
      >
        <div className="sidebar__top">
          <div
            className={classNames('sidebar__yield', {
              'sidebar__yield--collapseSideBar': collapseSideBar,
            })}
          >
            <div className="sidebar__tenant">
              <Tenant
                headline={`${user.firstname} ${user.lastname}`}
                strapline={user.tenant && user.tenant.name}
                avatar={
                  <div
                    onClick={() =>
                      setIsOpen(user && user.profileImage ? true : false)
                    }
                  >
                    <Avatar
                      badge={ICONS[user.type]}
                      url={user.profileImage}
                      size={undefined}
                    />
                    {isOpen && isOpen === true ? (
                      <Lightbox
                        mainSrc={user.profileImage}
                        onCloseRequest={() => setIsOpen(false)}
                      />
                    ) : null}
                  </div>
                }
              />
              {(user.roles[0].role.name === 'Home Owner' ||
                user.roles[0].role.name === 'Realtor' ||
                user.roles[0].role.name === 'Occupant') &&
                user.isDashboardAccess === false && (
                  <div style={{ marginTop: 20 }}>
                    <SuiteInfoHeader />
                  </div>
                )}
              {(user.roles[0].role.name === 'Home Owner' ||
                user.roles[0].role.name === 'Occupant') &&
              ishomeOwnwerHasAcessButton === true &&
              user.isDashboardAccess === false &&
              collapseSideBar === false ? (
                <>
                  {user.roles[0].role.name !== 'Home Owner' &&
                    user.roles[0].role.name !== 'Realtor' &&
                    user.roles[0].role.name !== 'Occupant' &&
                    user.isDashboardAccess === false && (
                      <div className="sidebar__search">
                        <Typeahead />
                      </div>
                    )}
                  <Overlay
                    showOverlay={serviceRequestFormIsVisible}
                    content={
                      <ServiceRequestForm
                        propertyDisabled={propertyDisabled}
                        serviceRequest={serviceRequest}
                        from={from}
                      />
                    }
                  />
                  <Button1
                    label="Create Request"
                    color="purple"
                    block
                    icon={
                      <FontAwesomeIcon
                        glyph="wrench"
                        weight="light"
                        size="small"
                      />
                    }
                    onClick={() => onSetServiceRequestFormIsVisible(true)}
                    disabled={undefined}
                    size={undefined}
                    outline={undefined}
                    weight={undefined}
                  ></Button1>
                </>
              ) : null}
            </div>
            {(user.roles[0].role.name === 'Admin' ||
              user.roles[0].role.name === 'Super Admin' ||
              user.roles[0].role.name === 'Property Developer') &&
            collapseSideBar === false &&
            user.isDashboardAccess === false ? (
              <>
                <div className="sidebar__search">
                  <Typeahead />
                </div>
                <ContactFormProvider>
                  <PropertiesForContactsProvider>
                    <Wizard
                      onClose={() => setShowOverlay(false)}
                      showOverlay={showOverlay}
                    />
                  </PropertiesForContactsProvider>
                </ContactFormProvider>
                <Button1
                  label="NEW"
                  onClick={() => {
                    setShowOverlay(true);
                    clearSelectedModulePermissionAction();
                    window.history.replaceState(
                      {},
                      null,
                      '/app/service-requests'
                    );
                  }}
                  color="purple"
                  block
                  disabled={undefined}
                  icon={undefined}
                  size={undefined}
                  outline={undefined}
                  weight={undefined}
                />
              </>
            ) : null}
          </div>
          <Navigation collapseSideBar={collapseSideBar} links={topLinks} />
          <div className="sidebar__spacer" />
          <Navigation collapseSideBar={collapseSideBar} links={bottomLinks} />
        </div>
        <div
          className={classNames('sidebar__bottom', {
            'sidebar__bottom--collapsed': collapseSideBar,
          })}
        >
          <div className="sidebar__brand">
            <div className="sidebar__logo">
              <img alt="juniper" src={Images.logo} height="18" />
            </div>
            <div className="sidebar__customer">
              {user.tenant && user.tenant.name}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
